/**
 * Break a card's body into scannable key points for the "Read it your way" reading mode. This is
 * a deterministic, client-side split (no model needed): collapse whitespace, cut on sentence
 * boundaries, and keep only substantial sentences so the result reads as clean takeaways rather
 * than arbitrary fragments.
 *
 * A card body is often just two dense sentences (common on cloud-sourced cards), which used to
 * fall short of the 3-point bar and hide the depth toggle entirely, so those cards looked
 * inconsistent next to richer ones. To keep the Quick scan consistent across the whole feed, when
 * the sentence split yields fewer than three points we break the longest sentences on coordinating
 * clause boundaries (", and", ", but", "; ", ", so", ", yet") that read cleanly as standalone
 * takeaways. Callers should still only offer the toggle when this returns 2 or more.
 */

// Coordinating boundaries whose trailing clause reads as a standalone sentence once split. We
// deliberately exclude subordinators (", which", ", as", ", after") because their tail clause
// ("Which...", "As...") does not stand on its own.
const CLAUSE_BOUNDARY = /,\s+(and|but|so|yet)\s+|;\s+/g;
const MIN_POINT = 24;
const MIN_CLAUSE = 22;

function splitSentences(text: string): string[] {
  return text
    // Split after sentence-ending punctuation that is followed by the start of a new sentence.
    // The negative lookbehind keeps initials and honorifics whole ("Robert F. Kennedy Jr.",
    // "Mr. Smith", "U.S. officials"), which otherwise shed fragments like "Robert F."
    .split(/(?<=[.!?])(?<!\b(?:[A-Z]|Jr|Sr|Dr|Mr|Mrs|Ms|St|vs|No)\.)\s+(?=["'(]?[A-Z0-9])/)
    .map((s) => s.trim())
    .filter(Boolean);
}

// Break one sentence at the clause boundary nearest its middle, returning two standalone-readable
// halves, or null when there is no boundary that leaves both halves substantial.
function splitOnClause(sentence: string): [string, string] | null {
  const mid = sentence.length / 2;
  let best: { index: number; length: number } | null = null;
  for (const match of sentence.matchAll(CLAUSE_BOUNDARY)) {
    const index = match.index ?? -1;
    if (index < 0) continue;
    const head = sentence.slice(0, index).trim();
    const tail = sentence.slice(index + match[0].length).trim();
    if (head.length < MIN_CLAUSE || tail.length < MIN_CLAUSE) continue;
    const distance = Math.abs(index - mid);
    if (!best || distance < best.length) best = { index, length: distance };
  }
  if (!best) return null;
  const match = [...sentence.matchAll(CLAUSE_BOUNDARY)].find((m) => m.index === best!.index)!;
  const head = sentence.slice(0, best.index).trim();
  const tail = sentence.slice(best.index + match[0].length).trim();
  return [tidyPoint(head), tidyPoint(tail)];
}

// Capitalize the first letter and ensure the point ends on terminal punctuation, so a clause
// lifted out of the middle of a sentence still reads as a clean, self-contained takeaway.
function tidyPoint(point: string): string {
  let p = point.trim().replace(/[,;]+$/, "");
  if (!p) return p;
  p = p.charAt(0).toUpperCase() + p.slice(1);
  if (!/[.!?]$/.test(p)) p += ".";
  return p;
}

export function keyPoints(body: string, max = 5): string[] {
  if (!body) return [];
  const clean = body.replace(/\s+/g, " ").trim();
  let parts = splitSentences(clean).filter((s) => s.length >= MIN_POINT).map(tidyPoint);

  // When the body is only one or two long sentences, break the longest splittable one on a
  // coordinating clause boundary until we reach three scannable points (or run out of splits).
  while (parts.length < 3) {
    let target = -1;
    for (let i = 0; i < parts.length; i++) {
      if (splitOnClause(parts[i]) && (target === -1 || parts[i].length > parts[target].length)) {
        target = i;
      }
    }
    if (target === -1) break;
    const split = splitOnClause(parts[target]);
    if (!split) break;
    parts.splice(target, 1, split[0], split[1]);
  }

  return parts.slice(0, max);
}
